import endpoints from 'dibs-endpoints';
import { getBuyerToken } from 'dibs-cookie-jar';
import { type MediaInfo } from './mediaService.ts';

type CropPayload = {
    x1: number;
    x2: number;
    y1: number;
    y2: number;
};
type ResizePayload = {
    newHeight: number;
    newWidth: number;
};

export type ProcessImagePayload = {
    crop: CropPayload;
    resize: ResizePayload;
};

type RequestBase = {
    apiType: string;
    order: number;
    type: 'CROP' | 'RESIZE';
};

type CropRequest = RequestBase & {
    rectangle: {
        topLeftX: number;
        topLeftY: number;
        bottomRightX: number;
        bottomRightY: number;
    };
};
type ResizeRequest = RequestBase & ResizePayload;

type DataList = Array<CropRequest | ResizeRequest>;

const buildCropRequest = ({ crop, resize }: ProcessImagePayload): { dataList: DataList } => {
    const dataList: DataList = [];
    let order = 1;

    if (crop) {
        dataList.push({
            apiType: 'photoCropRequest',
            order: order++,
            type: 'CROP',
            rectangle: {
                topLeftX: crop.x1,
                topLeftY: crop.y1,
                bottomRightX: crop.x2,
                bottomRightY: crop.y2,
            },
        });
    }

    if (resize) {
        const resizeData = {
            apiType: 'photoResizeRequest',
            order: order++,
            type: 'RESIZE' as const,
            ...resize,
        };

        dataList.push(resizeData);
    }

    return { dataList };
};

export const cropAndResizeImage = async ({
    id,
    data,
}: {
    id: number;
    data: ProcessImagePayload;
}): Promise<{ mediaInfo: MediaInfo | null }> => {
    const url = `${endpoints.processPhoto(id)}?${new URLSearchParams(
        getBuyerToken(document.cookie)
    )}`;
    const body = buildCropRequest(data);

    try {
        const response = await fetch(url, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            method: 'POST',
            body: JSON.stringify(body),
        });
        const { result } = await response.json();
        return {
            mediaInfo: {
                mediaId: result.id,
                mediaLocation: result.path,
                dimension: result.dimension,
                parentId: result.parent?.id,
            },
        };
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('error resizing an image', e);
        return { mediaInfo: null };
    }
};
